import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { apiError } from "@/services/api";
import { Button, Input, Label } from "@/components";

export function LoginForm() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const showDemo = process.env.NODE_ENV === "development" || process.env.REACT_APP_DEMO_MODE === "true";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ctx = await login(email, password);
      toast.success(t("login.loggedIn"));
      navigate(ctx.is_super_admin ? "/admin" : "/dashboard");
    } catch (err) {
      const key = { 401: "invalid", 403: "inactive", 429: "tooMany" }[err?.response?.status];
      toast.error(key ? t(`login.errors.${key}`) : (err?.response ? t("login.errors.generic") : apiError(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="login-form">
      <div className="flex flex-col gap-2">
        <Label htmlFor="email">{t("login.email")}</Label>
        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t("login.emailPlaceholder")} required data-testid="login-email" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="password">{t("login.password")}</Label>
        <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••" required data-testid="login-password" />
      </div>
      <Button type="submit" disabled={loading} size="lg" className="mt-2 h-12 w-full shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_44px_-8px_hsl(var(--primary)/0.85)] hover:brightness-[1.05] active:translate-y-0 active:scale-[0.99]" data-testid="login-submit">
        {loading ? t("login.submitting") : t("login.submit")}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        <Link to="/forgot-password" className="font-medium text-primary hover:underline" data-testid="to-forgot-link">
          {t("login.forgot")}
        </Link>
      </p>

      <div className="relative py-1 text-center">
        <span className="relative z-10 bg-background px-3 text-xs text-muted-foreground">{t("common.or")}</span>
        <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-border" />
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {t("login.noAccount")}{" "}
        <Link to="/register" className="font-medium text-primary hover:underline" data-testid="to-register-link">
          {t("login.createAccount")}
        </Link>
      </p>

      {showDemo && (
        <div className="mt-2 rounded-xl border border-border/70 bg-muted/40 p-3.5 text-xs text-muted-foreground" data-testid="demo-accounts-box">
          <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {t("login.demoTitle")}
          </p>
          <p className="font-mono text-[11px]">admin@arcquan.se · Demo!234</p>
          <p className="font-mono text-[11px]">tekniker@arcquan.se · Demo!234</p>
          <p className="mt-1 font-mono text-[11px] text-primary">demo@arcquan.com · Demo!234 (demo-konto)</p>
        </div>
      )}
    </form>
  );
}
